import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '@/firebase/config';
import axios from '@/lib/axiosClient';
import { toast } from 'sonner';

const API = '/api';

/**
 * Hook to detect user inactivity and automatically log out after idle timeout
 * @param {number} timeoutMinutes - Minutes of inactivity before auto-logout (default: 30)
 */
export function useIdleTimeout(timeoutMinutes = 30) {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Auto-logout sign-out error:', error);
    }
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Auto-logout backend error:', error);
    }
    toast.info('You have been logged out due to inactivity');
    // Bounce through the SSO portal so the global session is also
    // cleared — otherwise the next visit would silently re-sign-in.
    const portalUrl =
      process.env.REACT_APP_LOGIN_PORTAL_URL || 'https://login.someaditya.in/';
    window.location.href = `${portalUrl}?action=logout`;
  }, []);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    const timeoutMs = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(() => {
      const idleTime = Date.now() - lastActivityRef.current;
      
      // Double-check if still idle (user might have been active during timeout setup)
      if (idleTime >= timeoutMs) {
        handleLogout();
      }
    }, timeoutMs);
  }, [timeoutMinutes, handleLogout]);

  useEffect(() => {
    // Check if user is authenticated
    const user = auth.currentUser;
    if (!user) {
      return; // Don't set up idle detection if not authenticated
    }

    // Reset timer on any user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      resetTimer();
    };

    // Initialize timer
    resetTimer();

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer]);
}

